export const DASHBOARD_ACTION_TYPE = {
    Archive: 'archive',
    Create: 'create',
    Duplicate: 'duplicate',
    Export: 'export',
    Favourite: 'favourite',
    Move: 'move',
    PresentationMode: 'presentationMode',
    Rename: 'rename',
    Send: 'send',
    Share: 'share',
    EditLayout: 'editLayout',
    FullWidth: 'fullWidth',
    Informations: 'informations',
} as const;

export type DashboardActionType =
    (typeof DASHBOARD_ACTION_TYPE)[keyof typeof DASHBOARD_ACTION_TYPE];

export type DashboardActionCategory = 'organize' | 'share' | 'edit' | 'archive';

const SORTED_ACTION_CATEGORIES: DashboardActionCategory[] = [
    'organize',
    'share',
    'edit',
    'archive',
];

const ORGANIZE_ACTIONS = [
    DASHBOARD_ACTION_TYPE.Informations,
    DASHBOARD_ACTION_TYPE.Favourite,
    DASHBOARD_ACTION_TYPE.Move,
] as DashboardActionType[];

const SHARE_ACTIONS = [
    DASHBOARD_ACTION_TYPE.Share,
    DASHBOARD_ACTION_TYPE.Export,
    DASHBOARD_ACTION_TYPE.Send,
] as DashboardActionType[];

const EDIT_ACTIONS = [
    DASHBOARD_ACTION_TYPE.EditLayout,
    DASHBOARD_ACTION_TYPE.Rename,
    DASHBOARD_ACTION_TYPE.Duplicate,
    DASHBOARD_ACTION_TYPE.PresentationMode,
] as DashboardActionType[];

const ARCHIVE_ACTIONS = [
    DASHBOARD_ACTION_TYPE.Archive,
] as DashboardActionType[];

const SORTED_ACTIONS = [
    ...ORGANIZE_ACTIONS,
    ...SHARE_ACTIONS,
    ...EDIT_ACTIONS,
    ...ARCHIVE_ACTIONS,
] as DashboardActionType[];

const sortDashboardActions = (
    actions: DashboardActionType[],
): DashboardActionType[] =>
    actions.sort((a, b) => {
        const aIndex = SORTED_ACTIONS.indexOf(a);
        const bIndex = SORTED_ACTIONS.indexOf(b);
        return aIndex - bIndex;
    });

export const getOrganizeActions = (
    isPublicDashboard: boolean,
    allowMove: boolean,
    isAdminUser: boolean,
): DashboardActionType[] => {
    if (isPublicDashboard) return [];
    const organizeActions: DashboardActionType[] = [
        DASHBOARD_ACTION_TYPE.Informations,
        DASHBOARD_ACTION_TYPE.Favourite,
    ];
    if (allowMove && isAdminUser) {
        organizeActions.push(DASHBOARD_ACTION_TYPE.Move);
    }
    return sortDashboardActions(organizeActions);
};

export const getShareActions = (
    isPublicDashboard: boolean,
    canEdit: boolean,
    isHeader: boolean,
): DashboardActionType[] => {
    const shareActions: DashboardActionType[] = [DASHBOARD_ACTION_TYPE.Export];
    if (isPublicDashboard) return shareActions;
    if (isHeader) shareActions.push(DASHBOARD_ACTION_TYPE.Send);
    if (canEdit) shareActions.push(DASHBOARD_ACTION_TYPE.Share);
    return sortDashboardActions(shareActions);
};

export const getArchivedActions = (
    canEdit: boolean,
    isPublicDashboard: boolean,
    isSupportUser: boolean,
    hasEssential: boolean,
): DashboardActionType[] =>
    isPublicDashboard || (hasEssential && !isSupportUser) || !canEdit
        ? []
        : [DASHBOARD_ACTION_TYPE.Archive];

export const getEditActions = (
    isHeader: boolean,
    canEdit: boolean,
    isSupportUser: boolean,
    hasEssential: boolean,
    isPublicDashboard: boolean,
): DashboardActionType[] => {
    const editActions: DashboardActionType[] = [];
    if (isPublicDashboard) return editActions;
    if (canEdit) {
        editActions.push(DASHBOARD_ACTION_TYPE.Rename);
        if (isHeader)
            editActions.push(
                ...[
                    DASHBOARD_ACTION_TYPE.FullWidth,
                    DASHBOARD_ACTION_TYPE.EditLayout,
                    DASHBOARD_ACTION_TYPE.PresentationMode,
                ],
            );
    }
    if (!hasEssential || isSupportUser) {
        editActions.push(DASHBOARD_ACTION_TYPE.Duplicate);
    }
    return sortDashboardActions(editActions);
};

export const sortActionsCategories = (
    actionsByCategory: Record<DashboardActionCategory, DashboardActionType[]>,
) =>
    Object.keys(actionsByCategory)
        .filter((category) => actionsByCategory[category].length > 0)
        .sort((a, b) => {
            const aIndex = SORTED_ACTION_CATEGORIES.indexOf(a);
            const bIndex = SORTED_ACTION_CATEGORIES.indexOf(b);
            return aIndex - bIndex;
        });
